type VideoPlayerEventMap = {
    ready: void;
    start: void;
    play: void;
    playing: void;
    pause: void;
    waiting: void;
    seeking: void;
    seeked: void;
    timeupdate: void;
    durationchange: void;
    ratechange: void;
    ended: void;
    error: unknown;
    loadedmetadata: void;
    loadeddata: void;
    enterpictureinpicture: void;
    leavepictureinpicture: void;
    sourcechange: number | undefined;
};

export type VideoPlayerEvent = keyof VideoPlayerEventMap;

type Listener<E extends VideoPlayerEvent> = VideoPlayerEventMap[E] extends void
    ? () => void
    : (data: VideoPlayerEventMap[E]) => void;

export class VideoPlayerEventEmitter {
    private listeners = new Map<VideoPlayerEvent, Set<Listener<any>>>();

    on<E extends VideoPlayerEvent>(
        event: E,
        listener: Listener<E>,
    ): () => void {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event)!.add(listener);
        return () => this.off(event, listener);
    }

    off<E extends VideoPlayerEvent>(event: E, listener: Listener<E>) {
        this.listeners.get(event)?.delete(listener);
    }

    emit<E extends VideoPlayerEvent>(
        ...args: VideoPlayerEventMap[E] extends void
            ? [E]
            : [E, VideoPlayerEventMap[E]]
    ) {
        const [event, data] = args;
        this.listeners.get(event)?.forEach((listener) => listener(data));
    }
}
